import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import './Pagination.scss'

export const Pagination = ({
  className,
  page,
  pageSize,
  totalCount,
  strings,
  onChange,
  formatValues,
  'data-mu-test': dataAttributeTest
}) => {
  const pages = Math.ceil(totalCount / pageSize)
  const indexFrom = Math.min((page - 1) * pageSize + 1, totalCount)
  const indexTo = Math.min(totalCount, pageSize * page)

  const handleClickNext = () => {
    if (typeof onChange === 'function') {
      onChange(Math.min(pages, page + 1))
    }
  }

  const handleClickPrev = () => {
    if (typeof onChange === 'function') {
      onChange(Math.max(1, page - 1))
    }
  }

  return (
    <div
      className={classnames('c-pagination', className, {
        'c-pagination--no-pagi': pages === 1
      })}
    >
      {pages === 1 ? (
        <span>
          {indexFrom} - {indexTo} {strings.of} {totalCount}
        </span>
      ) : (
        <>
          <button
            className={classnames(
              'c-pagination__control',
              'c-pagination__control--prev',
              {
                'c-pagination__control--disabled': page === 1
              }
            )}
            onClick={page > 1 ? handleClickPrev : undefined}
            data-mu-test={classnames(
              'button--prev-page',
              dataAttributeTest && [`${dataAttributeTest}__button--prev-page`]
            )}
          />
          <div className='c-pagination__count'>
            {formatValues(indexFrom)} - {formatValues(indexTo)} {strings.of}{' '}
            {formatValues(totalCount)}
          </div>
          <button
            className={classnames(
              'c-pagination__control',
              'c-pagination__control--next',
              {
                'c-pagination__control--disabled': !Math.max(page - pages)
              }
            )}
            onClick={page < pages ? handleClickNext : undefined}
            data-mu-test={classnames(
              'button--next-page',
              dataAttributeTest && [`${dataAttributeTest}__button--next-page`]
            )}
          />
        </>
      )}
    </div>
  )
}

Pagination.defaultProps = {
  page: 1,
  pageSize: 10,
  strings: {
    of: 'of'
  },
  formatValues: val => val
}

/* istanbul ignore next */
Pagination.propTypes = {
  className: PropTypes.string,
  page: (props, propName, componentName) => {
    const value = props[propName]
    const isPresent = value !== undefined
    const isValid =
      typeof value === 'number' &&
      value <= Math.ceil(props.totalCount / props.pageSize) &&
      value > 0
    if (!isPresent) {
      return new Error(
        `Prop \`${propName}\` is required in ${componentName}\`.`
      )
    }
    if (!isValid) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to
        \`${componentName}\`. Prop value can't exceed (props.totalCount / props.pageSize).`
      )
    }
  },
  pageSize: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  strings: PropTypes.object,
  onChange: PropTypes.func,
  formatValues: PropTypes.func,
  /**
   * Additional values for data-attribute used for tests and tracking. The value is passed to
   * prev/next page buttons and 'items per page' select
   **/
  'data-mu-test': PropTypes.string
}
